import { HttpInterceptorFn, HttpRequest, HttpHandlerFn, HttpEvent, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, throwError, BehaviorSubject, of } from 'rxjs';
import { catchError, switchMap, filter, take } from 'rxjs/operators';
import { LoginService } from '../../features/auth/login.service';
import { Router } from '@angular/router';
import { environment } from '../../../environments/environment';

let isRefreshing = false;
let refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

export const authInterceptor: HttpInterceptorFn = (req: HttpRequest<any>, next: HttpHandlerFn): Observable<HttpEvent<any>> => {
  const loginService = inject(LoginService);
  const router = inject(Router);

  if (isRefreshing && !req.url.includes('/user/refreshtoken')) {
    return refreshTokenSubject.pipe(
      filter((token) => token !== null),
      take(1),
      switchMap((token) => next(addTokenHeader(req, token)))
    );
  }

  const token = loginService.getToken();
  let authReq = addTokenHeader(req, token);

  return next(authReq).pipe(
    switchMap((event: HttpEvent<any>) => {
      if (
        event instanceof HttpResponse &&
        event.body?.returncode === '301' &&
        !authReq.url.includes('/user/login') &&
        !authReq.url.includes('/user/refreshtoken')
      ) {
        return handle401Error(authReq, next, loginService, router);
      }
      return of(event);
    }),
    catchError((error) => {
      if (
        error instanceof HttpErrorResponse &&
        !authReq.url.includes('/user/login') &&
        !authReq.url.includes('/user/refreshtoken') &&
        error.status === 401
      ) {
        return handle401Error(authReq, next, loginService, router);
      }
      return throwError(() => error);
    })
  );
};

function addTokenHeader(request: HttpRequest<any>, token: string | null) {
  let headers = request.headers;
  
  if (environment.xapikey) {
    headers = headers.set('x-api-key', environment.xapikey);
  }

  if (token) {
    headers = headers.set('Authorization', `Bearer ${token}`);
  }

  return request.clone({ headers });
}

function handle401Error(request: HttpRequest<any>, next: HttpHandlerFn, loginService: LoginService, router: Router): Observable<HttpEvent<any>> {
  if (!isRefreshing) {
    isRefreshing = true;
    refreshTokenSubject.next(null);

    return loginService.refreshToken().pipe(
      switchMap((tokenResponse: any) => {
        isRefreshing = false;
        const newToken = tokenResponse?.data?.token;
        if (newToken) {
          refreshTokenSubject.next(newToken);
          return next(addTokenHeader(request, newToken));
        } else {
          loginService.logout();
          router.navigate(['/admin']);
          return throwError(() => new Error('Invalid refresh token response'));
        }
      }),
      catchError((err) => {
        isRefreshing = false;
        loginService.logout();
        router.navigate(['/admin']);
        return throwError(() => err);
      })
    );
  } else {
    return refreshTokenSubject.pipe(
      filter(token => token !== null),
      take(1),
      switchMap((token) => next(addTokenHeader(request, token)))
    );
  }
}
